import classNames from "classnames";
import { FormattedTextString } from "client/Misc/commonTypes";
import {
	setTrackingId
} from "client/Misc/utils";
import { flattenChildren, getReactNodeKey } from "client/Misc/reactUtils";
import {
	createContext,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	useContext,
	useMemo
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { Button } from "../Button/Button";
import { IconValue } from "../Icon/Icon";
import { Toolbar } from "../Toolbar/Toolbar";
import styles from "./TransparentButtonList.module.less";

type ResponsiveBehavior =
	| "wrap"
	| "more-menu";

export type TransparentButtonListProps = {
	isAlignCenter?: boolean;
	responsiveBehavior?: ResponsiveBehavior;
	trackingId?: string;
};

export const TransparentButtonList = (props: PropsWithChildren<TransparentButtonListProps>): ReactElement => {
	const {
		children,
		responsiveBehavior = "more-menu",
		isAlignCenter = false,
		trackingId
	} = props;

	const contextData: TransparentButtonListContextData = useMemo(
		(): TransparentButtonListContextData => {
			return {
				responsiveBehavior
			};
		},
		[responsiveBehavior]
	);

	const renderContent = (): ReactElement => {
		switch (responsiveBehavior) {
			case "wrap":
				return (
					<ButtonList
						gap={0}
						isWrapping
						isAlignCenter={isAlignCenter}
					>
						{children}
					</ButtonList>
				);
			case "more-menu":
				return (
					<Toolbar
						horizontalAlignment={isAlignCenter ? "center" : "left"}
						moreButtonKind="transparent-colored"
						hasNoMarginBetweenItems
					>
						{children}
						<Toolbar.SpaceBetween />
					</Toolbar>
				);
			default:
				never(responsiveBehavior);
		}
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_reserveSpaceRightSide]: responsiveBehavior === "wrap"
				}
			)}
		>
			<TransparentButtonListContext.Provider
				value={contextData}
			>
				{renderContent()}
			</TransparentButtonListContext.Provider>
		</div>
	);
};

type ItemProps = {
	text: string | FormattedTextString;
	icon?: IconValue;
	onClick?(): void;
	bubbleClick?: boolean;
	isDisabled?: boolean;
	trackingId?: string;
	rightIcon?: IconValue;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		responsiveBehavior
	} = useContext(TransparentButtonListContext);


	switch (responsiveBehavior) {
		case "wrap":
			return (
				<Button
					{...props}
					kind="transparent-for-list"
				/>
			);
		case "more-menu":
			return (
				<Toolbar.Button
					{...props}
					kind="transparent-for-list"
				/>
			);
		default:
			never(responsiveBehavior);
	}
};

TransparentButtonList.Item = Item;

TransparentButtonList.displayName = "TransparentButtonList";
Item.displayName = "Item";

type PredefinedGapKind = "small" | "medium" | "large";

const gapKindToStyles: { [key in PredefinedGapKind]: string } = {
	["small"]: styles.buttonListElement_smallGap,
	["medium"]: styles.buttonListElement_mediumGap,
	["large"]: styles.buttonListElement_largeGap
};

type ButtonListProps = {
	gap?: PredefinedGapKind | number;
	isWrapping?: boolean;
	isAlignCenter?: boolean;
};

const ButtonList = (props: PropsWithChildren<ButtonListProps>): ReactElement => {
	const {
		children,
		gap = "small",
		isWrapping = false,
		isAlignCenter = false
	} = props;

	const childrenArray: ReadonlyArray<ReactNode> = flattenChildren(children);

	return (
		<div
			className={classNames(
				styles.buttonList,
				{
					[styles.buttonList_wrapping]: isWrapping,
					[styles.buttonList_alignCenter]: isAlignCenter,
				}
			)}
		>
			{childrenArray.map((child, index) => (
				<div
					key={getReactNodeKey(child, index)}
					className={classNames(
						styles.buttonListElement,
						typeof gap === "string" && gapKindToStyles[gap]
					)}
					style={{
						marginRight: typeof gap === "number"
							&& index !== childrenArray.length - 1
								? gap
								: undefined
					}}
				>
					{ child }
				</div>
			))}
		</div>
	);
};

ButtonList.displayName = "ButtonList";

type TransparentButtonListContextData = {
	responsiveBehavior: ResponsiveBehavior;
};

const TransparentButtonListContext = createContext<TransparentButtonListContextData>({
	responsiveBehavior: "wrap"
});